// Dashboard Analytics PDF Export
// Generates a 2-page analytics report that mirrors the dashboard visuals.
// All drawing is programmatic (no html2canvas) so the output matches the
// metrics already loaded on screen — no extra network requests.

import { jsPDF } from 'jspdf';
import type { DashboardMetrics } from '../types/dashboard.types';

// ── Logo loader ───────────────────────────────────────────────────────────────
// Loads a public/ image via canvas → PNG dataURL. Returns null on failure so
// the PDF always generates even if the logo can't be fetched.
function loadLogoDataUrl(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

type RGB = [number, number, number];

// ── Brand / UI colours ────────────────────────────────────────────────────────
const C = {
  navy:    [10, 25, 41] as RGB,
  teal:    [32, 201, 151] as RGB,
  red:     [220, 38, 38] as RGB,
  amber:   [245, 158, 11] as RGB,
  yellow:  [202, 138, 4] as RGB,
  green:   [22, 163, 74] as RGB,
  blue:    [59, 130, 246] as RGB,
  purple:  [168, 85, 247] as RGB,
  emerald: [52, 211, 153] as RGB,
  gray:    [156, 163, 175] as RGB,
  white:   [255, 255, 255] as RGB,
  slate:   [15, 23, 42] as RGB,
  muted:   [100, 116, 139] as RGB,
  muted2:  [148, 163, 184] as RGB,
  border:  [226, 232, 240] as RGB, // also used as progress-bar track
};

const OUTCOME_COLORS: Record<string, RGB> = {
  emergency:           C.red,
  possible_concussion: C.amber,
  monitoring:          C.yellow,
  no_signs:            C.green,
};

const OUTCOME_LABELS: Record<string, string> = {
  emergency:           'Emergency',
  possible_concussion: 'Possible Concussion',
  monitoring:          'Monitoring',
  no_signs:            'No Signs',
};

const STATUS_COLORS: Record<string, RGB> = {
  new:                C.blue,
  under_review:       C.purple,
  referred_to_doctor: C.amber,
  awaiting_clearance: C.yellow,
  cleared_to_play:    C.emerald,
  closed:             C.gray,
};

const STATUS_LABELS: Record<string, string> = {
  new:                'New',
  under_review:       'Under Review',
  referred_to_doctor: 'Referred to Doctor',
  awaiting_clearance: 'Awaiting Clearance',
  cleared_to_play:    'Cleared to Play',
  closed:             'Closed',
};

// ── jsPDF shorthand setters ───────────────────────────────────────────────────
function setFill(doc: jsPDF, c: RGB)   { doc.setFillColor(c[0], c[1], c[2]); }
function setStroke(doc: jsPDF, c: RGB) { doc.setDrawColor(c[0], c[1], c[2]); }
function setTxt(doc: jsPDF, c: RGB)    { doc.setTextColor(c[0], c[1], c[2]); }

function drawCard(doc: jsPDF, x: number, y: number, w: number, h: number) {
  setFill(doc, C.white);
  setStroke(doc, C.border);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');
}

function fmtDuration(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
}

// ── Watermark ─────────────────────────────────────────────────────────────────
// Draws a large ghosted SY logo centred on the page (below all content).
// Uses the dark logo so it shows on the white page background.
// Opacity is achieved via jsPDF's GState API (requires jsPDF ≥2).
type DocWithGState = {
  GState: (opts: { opacity: number }) => unknown;
  setGState: (gs: unknown) => void;
  saveGraphicsState: () => void;
  restoreGraphicsState: () => void;
};
function drawWatermark(doc: jsPDF, pageW: number, pageH: number, darkLogoDataUrl: string) {
  // ~50% of page width, aspect-ratio locked, centred
  const wmW = pageW * 0.50;
  const wmH = wmW * (1279 / 1888);
  const wmX = (pageW - wmW) / 2;
  const wmY = (pageH - wmH) / 2;

  const d = doc as unknown as DocWithGState;
  d.saveGraphicsState();
  d.setGState(d.GState({ opacity: 0.07 })); // ~7% opacity → very subtle ghost
  doc.addImage(darkLogoDataUrl, 'PNG', wmX, wmY, wmW, wmH);
  d.restoreGraphicsState();
}

// ── Shared page header ────────────────────────────────────────────────────────
function drawPageHeader(
  doc: jsPDF, pageW: number,
  title: string, sub: string, dateStr: string,
  logoDataUrl?: string | null,
) {
  setFill(doc, C.navy);
  doc.rect(0, 0, pageW, 20, 'F');

  // SY logo — top-LEFT of the header bar (ratio 1888:1279 ≈ 1.476:1)
  const logoH = 10;
  const logoW = logoH * (1888 / 1279);
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 8, 5, logoW, logoH);
  }

  // Text starts after the logo
  const textX = logoDataUrl ? 8 + logoW + 5 : 12;

  setTxt(doc, C.teal);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.text('CHARLIE — CONCUSSION CO-PILOT', textX, 7);

  setTxt(doc, C.white);
  doc.setFontSize(11);
  doc.text(title, textX, 15);

  setTxt(doc, C.muted2);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dateStr}  ·  ${sub}`, pageW - 12, 15, { align: 'right' });
}

// ── KPI metric card ───────────────────────────────────────────────────────────
function drawKpiCard(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  label: string,
  value: string | number,
  subtitle: string | undefined,
  accent: RGB,
) {
  drawCard(doc, x, y, w, h);

  // Left accent strip
  setFill(doc, accent);
  doc.roundedRect(x + 3.5, y + 4, 1.5, 9, 0.5, 0.5, 'F');

  // Label
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  setTxt(doc, C.muted);
  doc.text(label, x + 7.5, y + 9);

  // Value
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text(String(value), x + 7.5, y + 20);

  // Subtitle
  if (subtitle) {
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    setTxt(doc, C.muted);
    doc.text(subtitle, x + 7.5, y + 26);
  }
}

// ── 30-day activity bar chart ─────────────────────────────────────────────────
function drawActivityChart(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  dailyCounts: { date: string; count: number }[],
) {
  drawCard(doc, x, y, w, h);

  const pL = 6, pR = 6, pT = 17, pB = 12;
  const cX = x + pL;
  const cY = y + pT;
  const cW = w - pL - pR;
  const cH = h - pT - pB;

  // Section title
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text('Assessment Activity', x + pL, y + 8);

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  setTxt(doc, C.muted);
  doc.text('Last 30 days', x + pL, y + 13);

  const total = dailyCounts.reduce((s, d) => s + d.count, 0);

  // Total count (top-right of card)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text(String(total), x + w - pR, y + 9, { align: 'right' });
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  setTxt(doc, C.muted);
  doc.text('Total', x + w - pR, y + 13.5, { align: 'right' });

  if (total === 0) {
    doc.setFontSize(7);
    setTxt(doc, C.muted);
    doc.text('No assessments in the last 30 days', x + w / 2, y + h / 2, { align: 'center' });
    return;
  }

  const maxCount = Math.max(...dailyCounts.map((d) => d.count), 1);
  const slotW = cW / dailyCounts.length;
  const gap = Math.max(0.3, slotW * 0.2);
  const barW = slotW - gap;
  const minBarH = 0.8;

  // Subtle horizontal grid lines
  setStroke(doc, C.border);
  doc.setLineWidth(0.12);
  [0.25, 0.5, 0.75, 1].forEach((f) => {
    const lY = cY + cH * (1 - f);
    doc.line(cX, lY, cX + cW, lY);
  });

  // Bars
  dailyCounts.forEach((day, i) => {
    const bX = cX + i * slotW + gap / 2;
    const bH = day.count > 0 ? Math.max((day.count / maxCount) * cH, minBarH) : minBarH;
    setFill(doc, day.count > 0 ? C.teal : C.border);
    doc.rect(bX, cY + cH - bH, barW, bH, 'F');
  });

  // X-axis date labels (~every 6 days)
  const interval = Math.max(1, Math.floor(dailyCounts.length / 5));
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  setTxt(doc, C.muted);
  dailyCounts.forEach((day, i) => {
    if (i % interval === 0) {
      const d = new Date(day.date + 'T00:00:00');
      const lbl = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
      doc.text(lbl, cX + i * slotW + slotW / 2, cY + cH + 5, { align: 'center' });
    }
  });
}

// ── Outcome breakdown (horizontal progress bars) ───────────────────────────────
function drawOutcomeBreakdown(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  outcomeCounts: { outcome: string; count: number }[],
  total: number,
) {
  drawCard(doc, x, y, w, h);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text('Assessment Outcomes', x + 6, y + 9);

  const rowH = (h - 14) / (outcomeCounts.length || 1);

  outcomeCounts.forEach(({ outcome, count }, i) => {
    const rY = y + 14 + i * rowH;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    const color = OUTCOME_COLORS[outcome] ?? C.gray;
    const label = OUTCOME_LABELS[outcome] ?? outcome.replace(/_/g, ' ');

    // Label (colour-coded to match dashboard)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    setTxt(doc, color);
    doc.text(label, x + 6, rY + 5);

    // Count + percentage (right-aligned)
    setTxt(doc, C.slate);
    doc.text(String(count), x + w - 6, rY + 5, { align: 'right' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    setTxt(doc, C.muted);
    doc.text(`${pct}%`, x + w - 6, rY + 9.5, { align: 'right' });

    // Progress bar
    const tW = w - 12;
    setFill(doc, C.border);
    doc.roundedRect(x + 6, rY + 11, tW, 2.5, 0.5, 0.5, 'F');
    if (pct > 0) {
      setFill(doc, color);
      doc.roundedRect(x + 6, rY + 11, tW * (pct / 100), 2.5, 0.5, 0.5, 'F');
    }
  });
}

// ── Case status pipeline (stacked bar + legend) ────────────────────────────────
function drawStatusPipeline(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  statusCounts: { status: string; count: number }[],
  total: number,
) {
  drawCard(doc, x, y, w, h);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text('Case Status Pipeline', x + 6, y + 9);

  // Stacked bar (matches dashboard)
  const barY = y + 13;
  const barH = 5;
  const barW = w - 12;
  let bX = x + 6;

  setFill(doc, C.border);
  doc.roundedRect(x + 6, barY, barW, barH, 1, 1, 'F');

  if (total > 0) {
    statusCounts.forEach(({ status, count }) => {
      if (!count) return;
      setFill(doc, STATUS_COLORS[status] ?? C.gray);
      doc.rect(bX, barY, (count / total) * barW, barH, 'F');
      bX += (count / total) * barW;
    });
  }

  // Legend grid — 3 columns, 2 rows (mirrors dashboard 2-column grid)
  const cols = 3;
  const legColW = (w - 12) / cols;
  const legRowH = (h - 22) / Math.ceil(statusCounts.length / cols);

  statusCounts.forEach(({ status, count }, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const lX = x + 6 + col * legColW;
    const lY = y + 22 + row * legRowH;
    const color = STATUS_COLORS[status] ?? C.gray;

    // Colour dot
    setFill(doc, color);
    doc.circle(lX + 1.5, lY + 1.5, 1.5, 'F');

    // Label
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    setTxt(doc, C.muted);
    doc.text(STATUS_LABELS[status] ?? status.replace(/_/g, ' '), lX + 5, lY + 3);

    // Count (right-aligned within column)
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    setTxt(doc, count > 0 ? color : C.muted);
    doc.text(String(count), lX + legColW - 2, lY + 3, { align: 'right' });
  });
}

// ── Top items list (horizontal bars, matches TopItemsList component) ───────────
function drawTopItems(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  title: string,
  items: { name: string; count: number }[],
  barColor: RGB,
) {
  drawCard(doc, x, y, w, h);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  setTxt(doc, C.slate);
  doc.text(title, x + 6, y + 9);

  if (!items.length) {
    doc.setFontSize(7);
    setTxt(doc, C.muted);
    doc.text('No data yet', x + w / 2, y + h / 2, { align: 'center' });
    return;
  }

  const maxCount = items[0].count;
  const rowH = (h - 14) / items.length;

  items.forEach(({ name, count }, i) => {
    const rY = y + 14 + i * rowH;
    const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;

    // Name — truncate to available width
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    setTxt(doc, C.slate);
    const maxW = w - 20;
    let label = name;
    while (label.length > 5 && doc.getTextWidth(label) > maxW) {
      label = label.slice(0, -4) + '...';
    }
    doc.text(label, x + 6, rY + 5);

    // Count (colour-coded, right-aligned)
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    setTxt(doc, barColor);
    doc.text(String(count), x + w - 6, rY + 5, { align: 'right' });

    // Progress bar
    const tW = w - 12;
    setFill(doc, C.border);
    doc.roundedRect(x + 6, rY + 7, tW, 2, 0.3, 0.3, 'F');
    if (pct > 0) {
      setFill(doc, barColor);
      doc.roundedRect(x + 6, rY + 7, tW * (pct / 100), 2, 0.3, 0.3, 'F');
    }
  });
}

// ── Medical disclaimer footer ─────────────────────────────────────────────────
function drawDisclaimer(doc: jsPDF, x: number, y: number, w: number) {
  setFill(doc, [255, 251, 235] as RGB);
  setStroke(doc, [253, 230, 138] as RGB);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, 8, 1, 1, 'FD');
  setTxt(doc, [146, 64, 14] as RGB);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Important: Charlie is an information tool — not a medical device. All suspected concussions must be assessed by a qualified medical professional.',
    x + 3, y + 5,
  );
}

// ── Main export function ──────────────────────────────────────────────────────
export async function generateDashboardPdf(metrics: DashboardMetrics): Promise<void> {
  const [logo, darkLogo] = await Promise.all([
    loadLogoDataUrl('/sy-logo-light.png'),
    loadLogoDataUrl('/sy-logo-dark.png'),
  ]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = 297;        // A4 landscape width (mm)
  const PH = 210;        // A4 landscape height (mm)
  const M  = 12;         // left/right margin
  const UW = PW - M * 2; // usable content width
  const dateStr = new Date().toLocaleDateString('en-AU', { dateStyle: 'medium' });
  const totalStr = `${metrics.totalAssessments} assessment${metrics.totalAssessments !== 1 ? 's' : ''}`;

  // ── Page 1: KPI cards + Activity chart + Outcomes + Status pipeline ──────────
  if (darkLogo) drawWatermark(doc, PW, PH, darkLogo);
  drawPageHeader(doc, PW, 'Assessment Dashboard Report', `${dateStr}  ·  ${totalStr}`, dateStr, logo);

  let y = 24;

  // KPI row — 4 equal-width cards
  const kW = (UW - 9) / 4; // 4 cards with 3 gaps of 3mm
  const kH = 32;
  const redPct = metrics.totalAssessments > 0
    ? `${Math.round((metrics.redFlagCount / metrics.totalAssessments) * 100)}% of assessments`
    : undefined;

  drawKpiCard(doc, M,              y, kW, kH, 'Total Assessments',  metrics.totalAssessments,            undefined,               C.teal);
  drawKpiCard(doc, M + (kW+3),     y, kW, kH, 'Red Flags Detected', metrics.redFlagCount,                redPct,                  C.red);
  drawKpiCard(doc, M + (kW+3)*2,   y, kW, kH, 'This Week',          metrics.assessmentsThisWeek,         'Assessments conducted', C.teal);
  drawKpiCard(doc, M + (kW+3)*3,   y, kW, kH, 'Avg Duration',       fmtDuration(metrics.avgDurationSeconds), 'Per assessment',    C.teal);

  y += kH + 4;

  // Activity chart (left ~2/3) + Outcome breakdown (right ~1/3)
  const chartRowH = 72;
  const aW = Math.round(UW * 0.64);
  const oW = UW - aW - 3;

  drawActivityChart(doc,    M,        y, aW, chartRowH, metrics.dailyCounts);
  drawOutcomeBreakdown(doc, M + aW + 3, y, oW, chartRowH, metrics.outcomeCounts, metrics.totalAssessments);

  y += chartRowH + 4;

  // Case status pipeline — full width
  const stH = 44;
  drawStatusPipeline(doc, M, y, UW, stH, metrics.statusCounts, metrics.totalAssessments);

  y += stH + 4;

  drawDisclaimer(doc, M, y, UW);

  // ── Page 2: Top reported items (3 columns) ────────────────────────────────────
  doc.addPage();
  if (darkLogo) drawWatermark(doc, PW, PH, darkLogo);
  drawPageHeader(doc, PW, 'Top Reported Items', `${dateStr}  ·  Red Flags · Observable Signs · Symptoms`, dateStr, logo);

  y = 24;

  const tH   = 105;
  const col3  = (UW - 6) / 3;

  drawTopItems(doc, M,              y, col3, tH, 'Top Red Flags',         metrics.topRedFlags,        C.red);
  drawTopItems(doc, M + col3 + 3,   y, col3, tH, 'Top Observable Signs',  metrics.topObservableSigns, C.amber);
  drawTopItems(doc, M + (col3+3)*2, y, col3, tH, 'Top Reported Symptoms', metrics.topSymptoms,        C.teal);

  y += tH + 4;
  drawDisclaimer(doc, M, y, UW);

  doc.save(`charlie-dashboard-${new Date().toISOString().slice(0, 10)}.pdf`);
}
